import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router';
import {
  billingAccountExtensionNameMsg,
  billingAccountNameMsg,
  billingAccountReferenceMsg,
  cityMsg,
  contactInfoMsg,
  deliveryMethodMsg,
  eInvoicingAddressMsg,
  eInvoicingOperatorMsg,
  emailInvoiceMsg,
  invalidEInvoiceAddressMsg,
  invoiceLanguageMsg,
  newBillingAgreementMsg,
  payerDetailsMsg,
  payerNameAndBusinessIdMsg,
  postalCodeMsg,
  searchWithCompanyNameOrBusinessIdMsg,
  selectMsg,
  streetAddressMsg,
  t,
} from '../../../../common/i18n/index.js';
import { CompanySearch } from '../../../../common/react-hook-form/fields/index.js';
import {
  getAutoPopulatedElectronicAddress,
  getDeliveryMethodDropDownOptions,
  getElectronicInvoiceOperatorDropDownOptions,
  getEmptyBillingAccount,
  getLanguageOptions,
} from '../../../../common/utils/billingAccountUtils.js';
import { ELECTRONIC_ADDRESS_REGEX } from '../../../../common/utils/validationUtils.js';
import { sortByActive } from '../../../../components/AutoComplete/utils.js';
import { BillingAccountDeliveryMethod } from '../../../../generated/api/billingAccountDeliveryMethod.js';
import { type BillingAccountNew, type OpenFormContext } from '../../OpenFormAnswers.js';
import { OpenFormDropdown } from '../../OpenFormComponents/OpenFormDropdown.js';
import { OpenFormBillingInvoiceDeliveryMethod } from '../../OpenFormComponents/OpenFormFormatted.js';
import { OpenFormGridBlock, OpenFormGridCol } from '../../OpenFormComponents/OpenFormGrid.js';
import { useOpenFormBillChannels } from '../../OpenFormHooks/useOpenFormBillChannels.js';
import { useOpenFormBillingAccounts } from '../../OpenFormHooks/useOpenFormBillingAccounts.js';
import { setValueOptions } from '../../OpenFormHooks/useOpenFormMethods.js';
import { type OpenFormOutlet } from '../../OpenFormLayout/OpenFormLayoutOutlet.js';
import { concat, getValue } from '../../OpenFormUtils.js';
import { OpenFormQuestionContact } from './OpenFormQuestionContact.js';
import { OpenFormQuestionInput } from './OpenFormQuestionInput.js';
import type { BillChannel } from '../../../../generated/api/billChannel.js';
import type { BillingAccount } from '../../../../generated/api/billingAccount.js';
import type { BillingAccountHeader } from '../../../../generated/api/billingAccountHeader.js';

const NewBillingAccount = ({
  account,
  channels,
  disabled,
  path,
}: {
  account: BillingAccountNew;
  channels: BillChannel[];
  disabled: boolean;
  path: (key?: string) => string;
}) => {
  const { setValue } = useFormContext();
  const sortedOperatorOptions = sortByActive(getElectronicInvoiceOperatorDropDownOptions(channels), item => item.label);

  useEffect(() => setValue(path(), account, setValueOptions), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (account.deliveryMethod === BillingAccountDeliveryMethod.ELECTRONIC && account.billElectronicOperator) {
      setValue(
        path('billElectronicAddress'),
        getAutoPopulatedElectronicAddress(account.billElectronicOperator, channels)
      );
    }
  }, [account.deliveryMethod, account.billElectronicOperator, channels, setValue, path]);

  return (
    <>
      <OpenFormGridBlock>
        <OpenFormGridCol>
          <CompanySearch
            label={t.FDE2(payerNameAndBusinessIdMsg)}
            disabled={disabled}
            fieldNames={{
              businessId: path('payerBusinessId'),
              businessName: path('payerName'),
              masterId: path('accountMasterId'),
              postalAddress: path('payerAddress.line1'),
              postCode: path('payerAddress.postalCode'),
              postOffice: path('payerAddress.postOffice'),
            }}
            helpText="&nbsp;"
            noResultsText={t.VRG9(searchWithCompanyNameOrBusinessIdMsg)}
            placeholder={account.payerName ? concat(account.payerName, account.payerBusinessId).join(', ') : undefined}
          />
        </OpenFormGridCol>
      </OpenFormGridBlock>
      <OpenFormGridBlock>
        <OpenFormQuestionInput
          label={t.YLAI(payerDetailsMsg)}
          name={path('payerNameExtension')}
          required={false}
          disabled={disabled}
          wrapGridRow={false}
          type="field"
        />
        <OpenFormQuestionInput
          label={t.DD38(streetAddressMsg)}
          name={path('payerAddress.line1')}
          required={true}
          disabled={disabled}
          wrapGridRow={false}
          type="field"
        />
        <OpenFormQuestionInput
          label={t.RUAW(postalCodeMsg)}
          name={path('payerAddress.postalCode')}
          required={true}
          disabled={disabled}
          wrapGridRow={false}
          type="field"
        />
        <OpenFormQuestionInput
          label={t.J0YE(cityMsg)}
          name={path('payerAddress.postOffice')}
          required={true}
          disabled={disabled}
          wrapGridRow={false}
          type="field"
        />
      </OpenFormGridBlock>
      <OpenFormGridBlock>
        <OpenFormQuestionContact
          colWidth={12}
          disabled={disabled}
          required={true}
          label={t.VYZS(contactInfoMsg)}
          name={path('billingContactId')}
          choices={account.billingContactId}
          // eslint-disable-next-line @typescript-eslint/naming-convention
          onSelect={({ Account, Email, FirstName, LastName, MobilePhone } = {}) => {
            setValue(path('billingContactAccountId'), Account?.Id);
            setValue(path('billingContactAccountName'), Account?.Name);
            setValue(path('billingContactEmail'), Email);
            setValue(path('billingContactName'), concat(FirstName, LastName).join(' ') || undefined);
            setValue(path('billingContactPhone'), MobilePhone);
          }}
        />
      </OpenFormGridBlock>
      <OpenFormGridBlock>
        <OpenFormQuestionInput
          label={t.RH6T(billingAccountNameMsg)}
          name={path('billingAccountName')}
          required={true}
          disabled={disabled}
          wrapGridRow={false}
          type="field"
        />
        <OpenFormQuestionInput
          label={t.KUTS(billingAccountExtensionNameMsg)}
          name={path('billingAccountExtensionName')}
          required={false}
          disabled={disabled}
          wrapGridRow={false}
          type="field"
        />
      </OpenFormGridBlock>
      <OpenFormGridBlock>
        <OpenFormQuestionInput
          label={t.XCF5(billingAccountReferenceMsg, '1')}
          name={path('customerReference1')}
          required={false}
          disabled={disabled}
          wrapGridRow={false}
          type="field"
        />
        <OpenFormQuestionInput
          label={t.XCF5(billingAccountReferenceMsg, '2')}
          name={path('customerReference2')}
          required={false}
          disabled={disabled}
          wrapGridRow={false}
          type="field"
        />
      </OpenFormGridBlock>
      <OpenFormGridBlock>
        <OpenFormDropdown
          label={t.G0QN(deliveryMethodMsg)}
          name={path('deliveryMethod')}
          disabled={disabled}
          items={getDeliveryMethodDropDownOptions(getEmptyBillingAccount(account.payerName ?? ''), channels)}
          onChange={() => {
            setValue(path('billElectronicOperator'), undefined);
            setValue(path('billElectronicOperatorLabel'), undefined);
            setValue(path('billElectronicAddress'), undefined);
            setValue(path('billReceiverEmail'), undefined);
          }}
        />
        <OpenFormDropdown
          label={t.A7DR(invoiceLanguageMsg)}
          name={path('billLanguage')}
          disabled={disabled}
          items={getLanguageOptions()}
        />
        {account.deliveryMethod === BillingAccountDeliveryMethod.ELECTRONIC ? (
          <>
            <OpenFormDropdown
              label={t.WVLB(eInvoicingOperatorMsg)}
              name={path('billElectronicOperator')}
              disabled={disabled}
              items={sortedOperatorOptions}
              onChange={(item?: { label?: string; value?: string }) => {
                setValue(path('billElectronicOperatorLabel'), item?.label);
                if (item?.value) {
                  const autoPopulatedAddress = getAutoPopulatedElectronicAddress(item.value, channels);
                  setValue(path('billElectronicAddress'), autoPopulatedAddress);
                }
              }}
            />
            <OpenFormQuestionInput
              label={t.OL7B(eInvoicingAddressMsg)}
              name={path('billElectronicAddress')}
              required={true}
              disabled={disabled}
              wrapGridRow={false}
              type="field"
              validate={value => (ELECTRONIC_ADDRESS_REGEX.test(value) ? undefined : t.NMXU(invalidEInvoiceAddressMsg))}
            />
          </>
        ) : account.deliveryMethod === BillingAccountDeliveryMethod.EMAIL ? (
          <OpenFormQuestionInput
            label={t.W1PP(emailInvoiceMsg)}
            name={path('billReceiverEmail')}
            required={true}
            disabled={disabled}
            wrapGridRow={false}
            type="email"
          />
        ) : null}
      </OpenFormGridBlock>
    </>
  );
};

const NewCombobox = () => {
  const value = 'NEW';
  const label = t.NCIJ(newBillingAgreementMsg);
  return { id: value, value, label, html: <>{label}</> };
};

const OldBillingAccount = ({ account, path }: { account?: BillingAccount; path: (key?: string) => string }) => {
  const { setValue } = useFormContext();
  useEffect(() => setValue(path(), account, setValueOptions), [account, path, setValue]);

  return !account ? null : (
    <OpenFormGridBlock>
      <OpenFormGridCol>
        <div>{account.billingAccountName}</div>
        <OpenFormBillingInvoiceDeliveryMethod account={account} />
      </OpenFormGridCol>
    </OpenFormGridBlock>
  );
};

const OldCombobox = ({
  billingAccountDisplayId,
  billingAccountId,
  billingAccountName,
  billingAccountExtensionName,
  payerName,
  payerNameExtension,
}: BillingAccountHeader) => {
  const label = concat(billingAccountDisplayId, payerNameExtension ?? payerName).join(' ');
  return {
    id: billingAccountId!,
    value: billingAccountId!,
    label: label,
    html: (
      <div>
        <div className="billing-account__label-container">
          <h5 className="billing-account__label-h5">{label}</h5>
        </div>
        {!billingAccountName ? null : (
          <div>
            {t.RH6T(billingAccountNameMsg)}: {billingAccountName}
          </div>
        )}
        {!billingAccountExtensionName ? null : (
          <div>
            {t.KUTS(billingAccountExtensionNameMsg)}: {billingAccountExtensionName}
          </div>
        )}
      </div>
    ),
  };
};

export const OpenFormQuestionBillingAccount = ({
  disabled,
  required,
  label,
  name,
  context,
  infoText,
}: {
  disabled: boolean;
  required: boolean;
  label: string;
  name: string;
  context: OpenFormContext | undefined;
  infoText?: string;
}) => {
  const { clearErrors, setValue } = useFormContext();
  const { account } = useOutletContext<Required<OpenFormOutlet>>();
  const accounts = useOpenFormBillingAccounts();
  const channels = useOpenFormBillChannels();
  const items = useMemo(() => concat(NewCombobox(), accounts.map(OldCombobox)), [accounts]);
  const selectedValue = useMemo(() => getValue(context?.choices), [context?.choices]);
  const billingAccountNew = useCallback((key?: string) => concat(name, 'billingAccountNew', key).join('.'), [name]);
  const billingAccountOld = useCallback((key?: string) => concat(name, 'billingAccountOld', key).join('.'), [name]);

  return (
    <OpenFormGridBlock colWidth={6} className="of-openform__view__choices__narrow" justifyCenterOuter={true}>
      <OpenFormGridCol className={classNames({ ['label--mandatory']: required })}>
        <CL.Combobox
          disabled={disabled}
          i18n_combobox_placeholderText={t.QRYV(selectMsg)}
          items={items}
          label={label}
          i18n_combobox_infoText={infoText}
          selectedItemFormat="label"
          selectedValue={selectedValue}
          onValueSelect={useCallback(
            (value?: string) => {
              clearErrors(name);
              setValue(name, { billingAccount: undefined, billingAccountData: undefined });
              setValue(name, value, setValueOptions);
            },
            [name, clearErrors, setValue]
          )}
        />
      </OpenFormGridCol>
      {selectedValue === items[0].id ? (
        channels.length ? (
          <NewBillingAccount
            account={context?.billingAccountNew ?? account.billingAccountNew}
            channels={channels}
            disabled={disabled}
            path={billingAccountNew}
          />
        ) : null
      ) : selectedValue ? (
        accounts.length ? (
          <OldBillingAccount
            account={accounts.find(({ billingAccountId }) => billingAccountId === selectedValue) as BillingAccount}
            path={billingAccountOld}
          />
        ) : null
      ) : null}
    </OpenFormGridBlock>
  );
};
